// Framework and third-party non-ui
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { makeStyles } from '@material-ui/core/styles';

// App components
import { init, suggest, search } from './TrailSearchController';
import { MapContext } from 'contexts/MapContext';
import { SearchContext } from 'contexts/SearchContext';
import {
  getAllTrailFeaturesByTrailId,
  getAllTrailFeaturesByManagedAreaName,
  getAllTrailFeaturesByManager,
  filterMapDataForTrailSelected,
  getAllNearbyTrails,
  fetchTrailsBySearchTerm,
} from 'data/map';

// JSON & Styles
import { StyledTrailSearch } from './TrailSearch-styled';

// Third-party components (buttons, icons, etc.)
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box, Card, IconButton, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';

const TrailSearch = ({ inputWidth }) => {
  // State
  const {
    mapView,
    setSidebarSelection,
    setManagedAreaFeatures,
    setManagerFeatures,
    setExtentHistory,
    resetCards,
    setAdvancedOptions,
  } = useContext(MapContext);
  const SearchState = useContext(SearchContext);

  const history = useHistory();

  const [vm, setVm] = useState(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);

  const [debouncedSearchTerm] = useDebounce(SearchState.state.searchTerm, 500);

  const searchBoxRef = useRef();

  // Style
  const useStyles = makeStyles((theme) => ({
    search: {
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: '#ffffff',
      marginRight: theme.spacing(2),
      marginLeft: 0,
      width: '440px',

      [theme.breakpoints.up('sm')]: {
        marginLeft: 0,
        width: '440px',
      },
      border: '1px solid #4D4D4D',
      borderRadius: '25px',
    },
    searchIcon: {
      padding: '4px',
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    inputRoot: {
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: '#4D4D4D',
    },
    inputInput: {
      padding: theme.spacing(1, 1, 1, 0),
      transition: theme.transitions.create('width'),
      width: '440px',
      height: '100%',
      [theme.breakpoints.up('md')]: {
        width: '440px',
      },
    },
    option: {
      fontFamily: 'Roboto',
      fontWeight: '400',
      alignItems: 'center',
      justifyContent: 'center',
    },
    managedAreaName: {
      fontFamily: 'Roboto',
      fontWeight: '200',
    },
    resultFlex: {
      display: 'flex',
      flexDirection: 'column',
      gap: '7px',
    },
    noOptions: {
      display: SearchState.suggestionResults?.length === 0 ? null : 'none',
      fontFamily: 'Open Sans',
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: '#4D4D4D',
    },
    searchItem: {
      paddingLeft: '16px',
      paddinRight: '16px',
      paddingTop: '4px',
      paddingBottom: '4px',
      width: '100%',
      display: 'flex',
      position: 'relative',
      boxSizing: 'border-box',
      textAlign: 'left',
      alignItems: 'center',
      paddingTop: '8px',
      paddingBottom: '8px',
      justifyContent: 'flex-start',
      textDecoration: 'none',
    },
    endAdornment: {
      paddingRight: '0 !important',
    },
  }));
  const classes = useStyles();

  // Actions
  const handleOpenSuggestions = (e) => {
    setIsSuggestionsOpen(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      async function getSuggestions() {
        const suggestions = await suggest({
          vm,
          value: SearchState.state.searchTerm,
        });
        SearchState.setSuggestionResults(suggestions);
      }
      if (vm && SearchState.state.searchTerm) {
        getSuggestions();
      }
    }
  };

  const onChange = async (event, selectedItem) => {
    resetCards();
    setIsSuggestionsOpen(false);
    if (selectedItem && selectedItem.text) {
      SearchState.setState({
        ...SearchState.state,
        history: [SearchState.state.searchTerm, ...SearchState.state.history],
        selection: selectedItem,
      });

      const value = selectedItem.text;

      // Call the SearchViewModel's search method
      let trailId;
      let managedAreaName;
      let features;
      let nearbyFeatures;

      const result = await search({ vm, value, mapView, ...selectedItem });
      const { optionValue } = selectedItem;

      const { searchResponse, searchTrails, searchExtentLocation } = result;

      if (searchResponse.results[0].source.layer) {
        // If the result was from the Trails layer...
        setAdvancedOptions({});
        if (searchResponse.results[1]?.results?.length && selectedItem.suggestResult.sourceIndex === 1) {
          //Primary Name + Primary Type Search on Trails Dissolved
          trailId = parseInt(selectedItem.trailId.replaceAll(',', '')); //name
          features = await getAllTrailFeaturesByTrailId(trailId, mapView.spatialReference);
          const trailGeometry =
            features[0].geometry?.paths?.length > 10 ? features[0].geometry.extent : features[0].geometry;
          nearbyFeatures = await getAllNearbyTrails(trailGeometry, trailId);
          await filterMapDataForTrailSelected(features);
          setExtentHistory({
            target: features[0], // Graphic object
            zoom: 11,
          });
          mapView.when(function () {
            mapView.goTo({
              target: features[0], // Graphic object
              zoom: 11,
            });
          });
          setSidebarSelection(['Trail', features[0].attributes, nearbyFeatures, features[0]]);
          // Update the route
          if (history.location.pathname !== '/details') {
            history.push({
              pathname: '/details',
            });
          }
        } //Manager search
        else if (optionValue === '(4)') {
          features = await getAllTrailFeaturesByManager(value);

          setExtentHistory(searchExtentLocation);
          setManagerFeatures(features);

          if (history.location.pathname !== '/details') {
            history.push({
              pathname: '/details',
            });
          }
        }
        //Managed Area Name Search
        else if (optionValue === '(3)') {
          features = await getAllTrailFeaturesByManagedAreaName(value);

          setExtentHistory(searchExtentLocation);
          setManagedAreaFeatures(features);

          if (history.location.pathname !== '/details') {
            history.push({
              pathname: '/details',
            });
          }
        }

        //  else if (
        //   searchResponse.results[0]?.results?.length &&
        //   selectedItem.suggestResult.sourceIndex === 0
        // ) {
        //   console.log("0")
        //   //Managed Area Name Search on Trails Dissolved
        //   // name = selectedItem.managedAreaName; //name
        //   managedAreaName = value;
        //   features = await getAllTrailFeaturesByManagedAreaName(
        //     managedAreaName
        //   );
        //   // Update the route
        //   getFeaturesExtent(features).then((unionedGeometry) => {
        //     setExtentHistory(unionedGeometry.extent);
        //     mapView.goTo(unionedGeometry.extent);
        //   });
        //   setSidebarSelection(null);
        //   setManagedAreaFeatures(features);
        //   console.log(features);
        //   if (history.location.pathname !== "/details") {
        //     history.push({
        //       pathname: "/details",
        //     });
        //   }
        // }
        else if (searchResponse.results[2]?.results?.length && selectedItem.suggestResult.sourceIndex === 2) {
          const name = selectedItem.suggestResult.text.split(' (')[0];
          const trailGeometry = searchExtentLocation;

          try {
            const trailResults = await fetchTrailsBySearchTerm(mapView, `UNIQUE_NAME='${name.replaceAll("'", "''")}'`);
            trailId = trailResults[0].attributes.PRIMARY_TRAIL_ID;

            features = await getAllTrailFeaturesByTrailId(trailId, mapView.spatialReference);
          } catch (err) {
            console.warn('Error occured calculating trail result.', err);
          }

          mapView.when(function () {
            setExtentHistory(searchExtentLocation);
            mapView.goTo(searchExtentLocation);
          });

          if (features?.length) {
            try {
              await filterMapDataForTrailSelected(features);

              nearbyFeatures = await getAllNearbyTrails(trailGeometry, trailId);
            } catch (err) {
              console.warn('Error occured calculating nearby features.', err);
              nearbyFeatures = [];
            }

            setSidebarSelection(['Trail', features[0].attributes, nearbyFeatures, features[0]]);
          } else {
            setSidebarSelection(null);
          }

          setManagedAreaFeatures(searchTrails);

          if (history.location.pathname !== '/details') {
            history.push({
              pathname: '/details',
            });
          }
        }
      } else {
        // If the result was from the locator...
        const layer = new FeatureLayer({
          url: 'https://gis.ohiodnr.gov/arcgis_site2/rest/services/testing/Composite_Locator_11212021/GeocodeServer',
          outFields: ['*'],
        });
        layer
          .when(() => {
            return layer.queryExtent();
          })
          .then((response) => {
            setExtentHistory(response.extent);
            mapView.goTo(response.extent);
          });
        // Update the route
        if (history.location.pathname !== '/details') {
          history.push({
            pathname: '/details',
          });
        }
      }

      //history.push('/details');
    } else {
      setExtentHistory(null);
      SearchState.setState({
        ...SearchState.state,
        selection: null,
      });
    }
  };

  const onSearchBack = () => {
    setIsSuggestionsOpen(true);

    const previousSearchTerms = [...SearchState.state.history];
    previousSearchTerms.shift();

    SearchState.setState({
      ...SearchState.state,
      searchTerm: SearchState.state.history[0],
      selection: null,
      history: previousSearchTerms,
    });
  };

  const onClearSearch = () => {
    SearchState.setState({
      searchTerm: '',
      selection: null,
      history: [],
    });

    SearchState.setSuggestionResults(null);
  };

  const getFeaturesExtent = async (features) => {
    return geometryEngine.union(features.map((feature) => feature.geometry.extent)).extent.expand(1.01);
  };

  // Effects

  // When the mapView is loaded from context, create a SearchViewModel
  useEffect(() => {
    async function loadVM() {
      const vm = await init({ view: mapView });
      setVm(vm);
    }
    if (mapView) {
      loadVM();
    }
  }, [mapView]);

  // When the search term changes, ask the view model for suggestions
  useEffect(() => {
    async function getSuggestions() {
      const suggestions = await suggest({ vm, value: debouncedSearchTerm });

      SearchState.setSuggestionResults(suggestions);
    }
    if (vm && debouncedSearchTerm) {
      getSuggestions();
    }
  }, [vm, debouncedSearchTerm]);

  // useEffect(() => {
  //   console.log(searchBoxRef?.current === document.activeElement);

  //   if (searchBoxRef?.current) {
  //     searchBoxRef.current.addEventListener();
  //     return () => {
  //       searchBoxRef.current.removeEventListener();
  //     };
  //   }
  // }, []);
  return (
    <StyledTrailSearch data-testid='TrailSearch'>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <Autocomplete
          classes={{
            root: classes.search, // class name, e.g. `classes-nesting-root-x`
            input: classes.inputRoot,
            noOptions: classes.noOptions,
            inputRoot: classes.endAdornment,
          }}
          open={isSuggestionsOpen}
          onOpen={handleOpenSuggestions}
          onBlur={() => {
            setIsSuggestionsOpen(false);
          }}
          noOptionsText={'Your search did not return any results.'}
          disableClearable={true}
          popupIcon={null}
          options={SearchState.suggestionResults?.length ? SearchState.suggestionResults : []}
          getOptionLabel={(option) => option.text}
          id='trail-search'
          inputValue={SearchState.state.searchTerm} // term in search bar
          onInputChange={(e, newInputValue) => {
            if (e || newInputValue?.length > 0) {
              SearchState.setState({
                ...SearchState.state,
                searchTerm: newInputValue,
              });
            }
          }}
          value={SearchState.state.selection} // selected from suggestions
          onChange={onChange}
          onKeyDown={handleKeyPress}
          getOptionSelected={(option, value) => option.text === value.text}
          clearOnBlur={false}
          renderOption={(option) => {
            return (
              <Box classes={{ root: classes.searchItem }} key={option.key}>
                <ListItemIcon>{option.displayIcon}</ListItemIcon>
                <ListItemText primary={option.displayText} />
              </Box>
            );
          }}
          renderInput={(params) => {
            /* params.inputProps.value */

            return (
              <TextField
                className={classes.inputInput}
                {...params}
                variant='standard'
                placeholder='Search for trails and places'
                fullWidth
                inputRef={searchBoxRef}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  startAdornment: (
                    <>
                      <InputAdornment position='start'>
                        {SearchState.state.history?.length > 0 ? (
                          <IconButton aria-label='search: back' onClick={onSearchBack}>
                            <ArrowBackIcon />
                          </IconButton>
                        ) : (
                          <SearchIcon style={{ marginLeft: '15px' }} />
                        )}
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {(SearchState.state.searchTerm?.length > 0 || SearchState.state.history?.length > 0) && (
                        <InputAdornment position='end'>
                          <IconButton aria-label='search: clear' onClick={onClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>
                  ),
                }}
              />
            );
          }}
          PaperComponent={({ children }) => <Card style={{ backgroundColor: 'white' }}>{children}</Card>}
          aria-label='trail-search'
        />
      </div>
    </StyledTrailSearch>
  );
};

export default TrailSearch;

/**
 *     trailInfo = suggestResult.text.split(";;");
    const text = trailInfo[0].charAt(0).toUpperCase() + trailInfo[0].slice(1);
 */
